import PropTypes from 'prop-types';
import { usePermissions } from '../hooks/usePermissions';

/**
 * PermissionGate — conditionally renders its children based on the current
 * user's permissions / roles.
 *
 * Props:
 *  - permission: single codename to require (string).
 *  - permissions: list of codenames; rendered when user has ANY of them
 *    (default) or ALL of them when `requireAll` is true.
 *  - requireAll: if true, user must have every entry of `permissions`.
 *  - requiredRoles: list of role names (backward compatibility).
 *  - showForSuperAdmin: when true, super admin always sees the content.
 *    Defaults to true so super admins aren't accidentally locked out.
 *  - fallback: node rendered when access is denied (defaults to null so the
 *    gated element simply disappears from the UI).
 */
const PermissionGate = (props) => {
  const {
    children,
    permission,
    permissions = [],
    requireAll = false,
    requiredRoles = [],
    fallback = null,
    showForSuperAdmin = true,
  } = props;

  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasAnyRole,
    isSuperAdmin,
  } = usePermissions();

  if (showForSuperAdmin && isSuperAdmin()) {
    return children;
  }

  if (requiredRoles.length > 0 && !hasAnyRole(requiredRoles)) {
    return fallback;
  }

  if (permission && !hasPermission(permission)) {
    return fallback;
  }

  if (permissions && permissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    if (!hasAccess) {
      return fallback;
    }
  }

  return children;
};

PermissionGate.propTypes = {
  children: PropTypes.node.isRequired,
  permission: PropTypes.string,
  permissions: PropTypes.arrayOf(PropTypes.string),
  requireAll: PropTypes.bool,
  requiredRoles: PropTypes.arrayOf(PropTypes.string),
  fallback: PropTypes.node,
  showForSuperAdmin: PropTypes.bool,
};

export default PermissionGate;
